import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import ayekartLogo from "../assets/images/ayekartLogo.png";
import { getEntityId, getUserRoleId, isSuperAdmin as checkSuperAdmin } from "../utils/api";
import { 
  LayoutGrid, 
  Settings, 
  LogOut,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  BarChart3,
  PieChart,
  TrendingUp,
  Activity,
  Briefcase,
  IndianRupee,
  Users,
  FileText,
  Target,
  Zap,
  LineChart,
  Shield
} from "lucide-react";
import "./Sidebar.css";

/**
 * Animated sidebar with dashboard tabs and navigation
 */
export default function Sidebar({
  tabs = [],
  user = {},
  idRoleMap = {},
  onTabChange,
  currentDashboardNumber,
}) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    // This effect is just for logging - active state is determined by dashboardNumber comparison in render
    if (tabs.length === 0) {
      return;
    }

    // Priority: currentDashboardNumber prop > localStorage
    const dashboardNumberToUse = currentDashboardNumber 
      ? currentDashboardNumber 
      : localStorage.getItem("dashboardNumber");
    
    if (dashboardNumberToUse) {
      const dashboardNum = typeof dashboardNumberToUse === 'number' 
        ? dashboardNumberToUse 
        : parseInt(dashboardNumberToUse, 10);
      if (!isNaN(dashboardNum)) {
        tabs.find(t => {
          const tabDashNum = typeof t.dashboardNumber === 'number' 
            ? t.dashboardNumber 
            : parseInt(t.dashboardNumber, 10);
          return tabDashNum === dashboardNum;
        });
      }
    }
  }, [tabs, currentDashboardNumber]);

  // Filter tabs based on search query
  const filteredTabs = useMemo(() => {
    if (!searchQuery.trim()) return tabs;
    const query = searchQuery.toLowerCase();
    return tabs.filter(tab => 
      tab.name.toLowerCase().includes(query)
    );
  }, [tabs, searchQuery]);

  const handleTabClick = (dashboardNumber, tabId) => {
    const dashboardNum = typeof dashboardNumber === 'number' 
      ? dashboardNumber 
      : parseInt(dashboardNumber, 10);
    
    if (isNaN(dashboardNum)) return;
    
    // Update localStorage - this is the single source of truth
    localStorage.setItem("dashboardNumber", dashboardNum.toString());
    
    // Notify parent component to update iframe without page reload
    if (onTabChange) {
      onTabChange(dashboardNum);
    } else {
      // Fallback: use navigate instead of window.location to prevent full reload
      navigate("/home", { replace: true });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("dashboardNumber");
  };

  const isSuperAdmin = checkSuperAdmin({ user }, idRoleMap);

  // Get user name from user object
  const getUserName = () => {
    if (!user?.user) return "";
    const u = user.user;
    if (u.first_name || u.last_name) {
      return `${u.first_name || ""} ${u.last_name || ""}`.trim();
    }
    return u.email?.split("@")[0] || "User";
  };

  const userName = getUserName();

  // Get icon for each tab based on index or name
  const getTabIcon = (tabName, index) => {
    const iconArray = [
      LayoutGrid,
      BarChart3,
      PieChart,
      TrendingUp,
      Activity,
      Briefcase,
      IndianRupee,
      Users,
      FileText,
      Target,
      Zap,
      LineChart
    ];
    
    // Try to match icon based on tab name keywords
    const name = tabName?.toLowerCase() || "";
    if (name.includes("investor") || name.includes("fund")) {
      return IndianRupee;
    }
    if (name.includes("metric") || name.includes("key")) {
      return Activity;
    }
    if (name.includes("overview") || name.includes("summary")) {
      return BarChart3;
    }
    if (name.includes("management") || name.includes("manage")) {
      return Briefcase;
    }
    if (name.includes("trend") || name.includes("growth")) {
      return TrendingUp;
    }
    if (name.includes("user") || name.includes("people")) {
      return Users;
    }
    if (name.includes("report") || name.includes("document")) {
      return FileText;
    }
    if (name.includes("target") || name.includes("goal")) {
      return Target;
    }
    if (name.includes("analytics") || name.includes("analysis")) {
      return LineChart;
    }
    
    // Default: cycle through icons based on index
    return iconArray[index % iconArray.length];
  };

  return (
    <aside className={`sidebar ${isCollapsed ? "sidebar-collapsed" : ""}`}>
      <div className="sidebar-header">
        <Link to="/home" className="sidebar-logo">
          <img src={ayekartLogo} alt="Ayekart Logo" />
        </Link>
        <button
          className="sidebar-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label="Toggle sidebar"
        >
          {isCollapsed ? (
            <ChevronRight size={18} strokeWidth={2} className="sidebar-toggle-icon" />
          ) : (
            <ChevronLeft size={18} strokeWidth={2} className="sidebar-toggle-icon" />
          )}
        </button>
      </div>

      {/* User Info Section */}
      {!isCollapsed && userName && (
        <div className="sidebar-user-info">
          <div className="sidebar-user-avatar">
            <User size={20} strokeWidth={2} />
          </div>
          <div className="sidebar-user-details">
            <div className="sidebar-user-name">{userName}</div>
            <div className="sidebar-user-email">{user?.user?.email || ""}</div>
          </div>
        </div>
      )}

      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <div className="sidebar-section-title">Dashboards</div>
          
          {/* Search Bar */}
          {!isCollapsed && (
            <div className="sidebar-search-wrapper">
              <div className={`sidebar-search ${isSearchFocused ? "focused" : ""}`}>
                <Search size={18} strokeWidth={2} className="sidebar-search-icon" />
                <input
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Search dashboards..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                />
                {searchQuery && (
                  <button
                    className="sidebar-search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <X size={16} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Scrollable Tabs */}
          <div className="sidebar-tabs-container">
            <div className="sidebar-tabs">
              {filteredTabs.length > 0 ? (
                filteredTabs.map((item, index) => {
                  // Single source of truth: Use dashboardNumber from localStorage or currentDashboardNumber prop
                  const dashboardNumberToCheck = currentDashboardNumber 
                    ? (typeof currentDashboardNumber === 'number' ? currentDashboardNumber : parseInt(currentDashboardNumber, 10))
                    : (() => {
                        const stored = localStorage.getItem("dashboardNumber");
                        return stored ? parseInt(stored, 10) : null;
                      })();
                  
                  const itemDashNum = typeof item.dashboardNumber === 'number' 
                    ? item.dashboardNumber 
                    : parseInt(item.dashboardNumber, 10);
                  
                  // Only active if dashboardNumber matches exactly (single source of truth)
                  const isActive = dashboardNumberToCheck !== null && 
                                  !isNaN(dashboardNumberToCheck) && 
                                  !isNaN(itemDashNum) &&
                                  itemDashNum === dashboardNumberToCheck;
                  
                  const uniqueKey = getEntityId(item) || `tab-${itemDashNum}-${index}`;
                  const TabIcon = getTabIcon(item.name, index);
                  return (
                    <button
                      key={uniqueKey}
                      type="button"
                      className={`sidebar-tab ${isActive ? "active" : ""}`}
                      onClick={() => handleTabClick(item.dashboardNumber, getEntityId(item))}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <span className="sidebar-tab-icon">
                        <TabIcon size={isCollapsed ? 24 : 22} strokeWidth={2} />
                      </span>
                      <span className="sidebar-tab-text">{item.name}</span>
                      {isActive && (
                        <span key={`indicator-${uniqueKey}`} className="sidebar-tab-indicator"></span>
                      )}
                    </button>
                  );
                })
              ) : tabs.length > 0 ? (
                <div key="no-dashboards-found" className="sidebar-empty">
                  <p>No dashboards found</p>
                </div>
              ) : (
                <div key="no-dashboards-available" className="sidebar-empty">
                  <p>No dashboards available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sidebar-section sidebar-section-bottom">
          <div className="sidebar-section-title sidebar-section-title-small">settings</div>
          <div className="sidebar-nav-links">
            {isSuperAdmin && (
              <>
                <Link
                  key="roles-link"
                  to="/roles"
                  className="sidebar-nav-link"
                >
                  <span className="sidebar-nav-icon">
                    <Shield size={isCollapsed ? 24 : 22} strokeWidth={2} />
                  </span>
                  <span className="sidebar-nav-text">Roles</span>
                </Link>
                <Link
                  key="tabs-link"
                  to="/tabs"
                  className="sidebar-nav-link"
                >
                  <span className="sidebar-nav-icon">
                    <LayoutGrid size={isCollapsed ? 24 : 22} strokeWidth={2} />
                  </span>
                  <span className="sidebar-nav-text">Dashboard Management</span>
                </Link>
              </>
            )}
            <Link
              key="reset-password-link"
              to="/resetPassword"
              className="sidebar-nav-link"
            >
              <span className="sidebar-nav-icon">
                <Settings size={isCollapsed ? 24 : 22} strokeWidth={2} />
              </span>
              <span className="sidebar-nav-text">Reset Password</span>
            </Link>
            <Link
              key="logout-link"
              to="/login"
              className="sidebar-nav-link sidebar-nav-link-logout"
              onClick={handleLogout}
            >
              <span className="sidebar-nav-icon">
                <LogOut size={isCollapsed ? 24 : 22} strokeWidth={2} />
              </span>
              <span className="sidebar-nav-text">Logout</span>
            </Link>
          </div>
        </div>
      </nav>
    </aside>
  );
}
